import { Grid, Divider, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch, MenuItem, Button, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FC, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAccountById, updateAccount, fetchBuyerOrSupplierByName, fetchAccountSettings, updateAccountSettings } from "../../store/accountThunk";
import EmailListEditor from './EmailListEditor';

// Reports that support the Share-by-email feature. `key` matches the backend REPORT_KEY.
const SHARE_REPORT_OPTIONS: { key: string; label: string }[] = [
  { key: 'gstr1', label: 'GSTR-1 Sales Summary' },
  { key: 'tds', label: 'TDS Payable' },
  { key: 'dayBook', label: 'Day Book' },
  { key: 'profitLoss', label: 'Profit & Loss' },
  { key: 'balanceSheet', label: 'Balance Sheet' },
];

// WhatsApp notification toggles shown in the Settings tab (keys must match the backend model).
const WHATSAPP_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'invoiceCreated', label: 'Invoice created' },
  { key: 'paymentReceived', label: 'Payment received' },
  { key: 'paymentDueReminder', label: 'Payment due reminder' },
  { key: 'paymentOverdue', label: 'Payment overdue alert (the day after due date)' },
  { key: 'businessSummary', label: 'Daily business summary' },
];

// Email notification toggles (keys must match the backend emailNotifications model).
const EMAIL_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'businessSummary', label: 'Daily business summary' },
  { key: 'shareReports', label: 'Share reports to recipients (when you Share a report)' },
];

// Selectable "days before due date" values for the payment due reminder.
const DUE_REMINDER_DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 10, 15];
const DEFAULT_DUE_REMINDER_DAYS = 2;

// Basic email shape check for the Business Summary extra-recipient input.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Phone: 8–15 digits, optional leading +. Kept permissive to allow country codes.
const PHONE_RE = /^\+?\d{8,15}$/;

// Which card group to render. The Settings page tabs one section in at a time.
export type SettingsSection = 'notifications' | 'cashSale';

interface SettingsProps {
  section: SettingsSection;
}

const Settings: FC<SettingsProps> = ({ section }) => {
  const dispatch = useAppDispatch();

  const showNotifications = section === 'notifications';
  const showCashSale = section === 'cashSale';

  // Cash sale account state
  const [options, setOptions] = useState<any[]>([]);
  const [selectedCashSale, setSelectedCashSale] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [savingCashSale, setSavingCashSale] = useState(false);

  // WhatsApp notification toggles
  const [waNotifications, setWaNotifications] = useState<any>(null);
  const [savingWaKey, setSavingWaKey] = useState<string | null>(null);

  // Email notification toggles
  const [emailNotifications, setEmailNotifications] = useState<any>(null);
  const [savingEmailKey, setSavingEmailKey] = useState<string | null>(null);

  // How many days before the due date the payment due reminder is sent.
  const [dueReminderDays, setDueReminderDays] = useState<number>(DEFAULT_DUE_REMINDER_DAYS);
  const [savingDueReminderDays, setSavingDueReminderDays] = useState(false);

  // Extra Business Summary recipients (in addition to the account's own email/WhatsApp number).
  const [summaryEmails, setSummaryEmails] = useState<string[]>([]);
  const [summaryPhones, setSummaryPhones] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [savingRecipients, setSavingRecipients] = useState(false);

  // Report share recipients: a master list applied to all reports, plus optional per-report lists.
  const [masterEmails, setMasterEmails] = useState<string[]>([]);
  const [perReportEmails, setPerReportEmails] = useState<Record<string, string[]>>({});
  const [selectedReportKey, setSelectedReportKey] = useState<string>(SHARE_REPORT_OPTIONS[0].key);
  const [savingShare, setSavingShare] = useState(false);
  // Which recipient mode the user is editing — one at a time to avoid confusion.
  const [shareMode, setShareMode] = useState<'master' | 'specific'>('master');

  const accountDetails = useAppSelector(state => state.account.accountDetails);

  // Debounce search input
  useEffect(() => {
    if (!showCashSale) return;
    setIsSearching(true);
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm, showCashSale]);

  // Fetch buyers when debounced term changes
  useEffect(() => {
    if (!accountDetails?._id || !showCashSale) return;
    setIsSearching(true);
    const fetchData = async () => {
      try {
        const queryString = `name=${debouncedTerm}&&searchFor=buyer`;
        const data: any = await dispatch(fetchBuyerOrSupplierByName(queryString)).unwrap();
        if (data) {
          const filtered = data.accounts.filter((item: any) => item._id !== accountDetails._id);
          setOptions(debouncedTerm ? filtered : filtered.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching buyers:", error);
      }
      setIsSearching(false);
    };
    fetchData();
  }, [debouncedTerm]);

  // Handle buyer selection
  const handleCashSaleChange = async (_event: any, value: any) => {
    setSelectedCashSale(value);
    setSavingCashSale(true);
    try {
      const formData = new FormData();
      formData.append('defaultCashSaleAccount', value?._id || '');
      const responseData: any = await dispatch(updateAccount({
        id: accountDetails._id,
        accountPayload: formData as any,
      }));
      if (responseData && responseData?.status === 200) {
        dispatch(fetchAccountById(accountDetails._id));
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setSavingCashSale(false);
    }
  };

  // Load WhatsApp notification settings when the account is available.
  useEffect(() => {
    if (!accountDetails?._id || !showNotifications) return;
    const loadSettings = async () => {
      try {
        const res: any = await dispatch(fetchAccountSettings(accountDetails._id)).unwrap();
        if (res?.settings?.whatsappNotifications) {
          setWaNotifications(res.settings.whatsappNotifications);
        }
        if (res?.settings?.emailNotifications) {
          setEmailNotifications(res.settings.emailNotifications);
        }
        if (res?.settings?.paymentDueReminderDays) {
          setDueReminderDays(res.settings.paymentDueReminderDays);
        }
        if (res?.settings?.businessSummaryRecipients) {
          setSummaryEmails(res.settings.businessSummaryRecipients.emails || []);
          setSummaryPhones(res.settings.businessSummaryRecipients.phoneNumbers || []);
        }
        if (res?.settings?.reportShareRecipients) {
          const share = res.settings.reportShareRecipients;
          setMasterEmails(share.master?.emails || []);
          const per: Record<string, string[]> = {};
          SHARE_REPORT_OPTIONS.forEach(({ key }) => {
            per[key] = share.perReport?.[key]?.emails || [];
          });
          setPerReportEmails(per);
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };
    loadSettings();
  }, [accountDetails?._id, showNotifications]);

  // Toggle a single email notification type and persist it.
  const handleEmailToggle = async (key: string, checked: boolean) => {
    const previous = emailNotifications;
    setEmailNotifications({ ...(emailNotifications || {}), [key]: checked }); // optimistic
    setSavingEmailKey(key);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        emailNotifications: { [key]: checked },
      })).unwrap();
      if (res?.settings?.emailNotifications) {
        setEmailNotifications(res.settings.emailNotifications);
      }
    } catch (error) {
      console.error('Error updating email notification setting:', error);
      setEmailNotifications(previous); // revert on failure
    } finally {
      setSavingEmailKey(null);
    }
  };

  // Toggle a single WhatsApp notification type and persist it.
  const handleWaToggle = async (key: string, checked: boolean) => {
    const previous = waNotifications;
    setWaNotifications({ ...(waNotifications || {}), [key]: checked }); // optimistic
    setSavingWaKey(key);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        whatsappNotifications: { [key]: checked },
      })).unwrap();
      if (res?.settings?.whatsappNotifications) {
        setWaNotifications(res.settings.whatsappNotifications);
      }
    } catch (error) {
      console.error('Error updating WhatsApp notification setting:', error);
      setWaNotifications(previous); // revert on failure
    } finally {
      setSavingWaKey(null);
    }
  };

  // Change how many days before the due date the payment due reminder fires, and persist it.
  const handleDueReminderDaysChange = async (days: number) => {
    const previous = dueReminderDays;
    setDueReminderDays(days); // optimistic
    setSavingDueReminderDays(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        paymentDueReminderDays: days,
      })).unwrap();
      if (res?.settings?.paymentDueReminderDays) {
        setDueReminderDays(res.settings.paymentDueReminderDays);
      }
    } catch (error) {
      console.error('Error updating payment due reminder days:', error);
      setDueReminderDays(previous); // revert on failure
    } finally {
      setSavingDueReminderDays(false);
    }
  };

  // Persist the current recipient lists. Called after any add/remove; reverts on failure.
  const persistRecipients = async (emails: string[], phones: string[], previous: { emails: string[]; phones: string[] }) => {
    setSavingRecipients(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        businessSummaryRecipients: { emails, phoneNumbers: phones },
      })).unwrap();
      // Sync from the server-normalized lists (lowercased/de-duped/validated).
      if (res?.settings?.businessSummaryRecipients) {
        setSummaryEmails(res.settings.businessSummaryRecipients.emails || []);
        setSummaryPhones(res.settings.businessSummaryRecipients.phoneNumbers || []);
      }
    } catch (error) {
      console.error('Error updating business summary recipients:', error);
      setSummaryEmails(previous.emails); // revert on failure
      setSummaryPhones(previous.phones);
    } finally {
      setSavingRecipients(false);
    }
  };

  // Add a validated email to the recipient list and persist.
  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) { setEmailError('Enter a valid email address'); return; }
    if (summaryEmails.includes(email)) { setEmailError('This email is already added'); return; }
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = [...summaryEmails, email];
    setSummaryEmails(next); // optimistic
    setEmailInput('');
    setEmailError(null);
    persistRecipients(next, summaryPhones, previous);
  };

  // Add a validated phone number to the recipient list and persist.
  const handleAddPhone = () => {
    const phone = phoneInput.trim();
    if (!phone) return;
    if (!PHONE_RE.test(phone)) { setPhoneError('Enter a valid phone number (8–15 digits)'); return; }
    if (summaryPhones.includes(phone)) { setPhoneError('This number is already added'); return; }
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = [...summaryPhones, phone];
    setSummaryPhones(next); // optimistic
    setPhoneInput('');
    setPhoneError(null);
    persistRecipients(summaryEmails, next, previous);
  };

  // Remove an email from the recipient list and persist.
  const handleRemoveEmail = (email: string) => {
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = summaryEmails.filter((e) => e !== email);
    setSummaryEmails(next); // optimistic
    persistRecipients(next, summaryPhones, previous);
  };

  // Remove a phone number from the recipient list and persist.
  const handleRemovePhone = (phone: string) => {
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = summaryPhones.filter((p) => p !== phone);
    setSummaryPhones(next); // optimistic
    persistRecipients(summaryEmails, next, previous);
  };

  // Persist the master report-share recipient list; optimistic with revert on failure.
  const handleMasterEmailsChange = async (next: string[]) => {
    const previous = masterEmails;
    setMasterEmails(next); // optimistic
    setSavingShare(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        reportShareRecipients: { master: { emails: next } },
      })).unwrap();
      if (res?.settings?.reportShareRecipients?.master) {
        setMasterEmails(res.settings.reportShareRecipients.master.emails || []);
      }
    } catch (error) {
      console.error('Error updating master report recipients:', error);
      setMasterEmails(previous); // revert on failure
    } finally {
      setSavingShare(false);
    }
  };

  // Persist a per-report recipient list for the given report key; optimistic with revert on failure.
  const handlePerReportEmailsChange = async (reportKey: string, next: string[]) => {
    const previous = perReportEmails[reportKey] || [];
    setPerReportEmails((prev) => ({ ...prev, [reportKey]: next })); // optimistic
    setSavingShare(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        reportShareRecipients: { perReport: { [reportKey]: { emails: next } } },
      })).unwrap();
      const serverList = res?.settings?.reportShareRecipients?.perReport?.[reportKey]?.emails;
      if (serverList) {
        setPerReportEmails((prev) => ({ ...prev, [reportKey]: serverList }));
      }
    } catch (error) {
      console.error('Error updating per-report recipients:', error);
      setPerReportEmails((prev) => ({ ...prev, [reportKey]: previous })); // revert on failure
      setSavingShare(false);
    } finally {
      setSavingShare(false);
    }
  };

  // Sync selected value from account details
  useEffect(() => {
    let active = true;
    const settingsInfo = () => {
      try {
        if (active) {
          if (accountDetails.defaultCashSaleAccount && typeof accountDetails.defaultCashSaleAccount === 'object') {
            setSelectedCashSale(accountDetails.defaultCashSaleAccount);
          } else {
            setSelectedCashSale(null);
          }
        }
      } catch (error: any) {
        console.log('Setting catch block called');
      }
    };
    settingsInfo();
    return () => { active = false; };
  }, [accountDetails]);

  return (
    <Grid container spacing={2} alignItems="stretch">
      {/* Notifications tab, left column: WhatsApp Notifications */}
      {showNotifications && <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%' }}>
          <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>WhatsApp Notifications</h5>
          <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
            Choose which WhatsApp messages this account sends. Each type can be turned on or off independently.
          </h5>
          <Grid container spacing={1}>
            {WHATSAPP_NOTIFICATION_OPTIONS.map((option) => (
              <Grid item xs={12} key={option.key}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 480 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!waNotifications?.[option.key]}
                        onChange={(e) => handleWaToggle(option.key, e.target.checked)}
                        disabled={waNotifications === null || savingWaKey === option.key}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                  />
                  {savingWaKey === option.key ? <CircularProgress size={16} /> : null}
                </div>

                {/* Days-before selector for the payment due reminder. Shown only when that type is on. */}
                {option.key === 'paymentDueReminder' && !!waNotifications?.paymentDueReminder ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 46, marginTop: 2, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: '#9da7b7' }}>Send</span>
                    <TextField
                      select
                      size="small"
                      variant="standard"
                      value={dueReminderDays}
                      onChange={(e) => handleDueReminderDaysChange(Number(e.target.value))}
                      disabled={savingDueReminderDays}
                      sx={{ width: 64 }}
                      InputProps={{ disableUnderline: false }}
                    >
                      {DUE_REMINDER_DAY_OPTIONS.map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </TextField>
                    <span style={{ fontSize: 13, color: '#9da7b7' }}>day(s) before due date</span>
                    {savingDueReminderDays ? <CircularProgress size={14} /> : null}
                  </div>
                ) : null}
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>}

      {/* Default Cash Sale Account tab */}
      {showCashSale && <Grid item xs={12} md={6}>
            <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10 }}>
              <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Default Cash Sale Account</h5>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
                Select a buyer account to use as the default for cash sales
              </h5>
              <div style={{ display: 'flex', alignItems: 'center', maxWidth: 400 }}>
                <Autocomplete
                  sx={{ width: '100%' }}
                  value={selectedCashSale}
                  onChange={handleCashSaleChange}
                  onInputChange={(_e, value) => setSearchTerm(value)}
                  options={options}
                  getOptionLabel={(option: any) => option.name || ''}
                  isOptionEqualToValue={(option: any, value: any) => option._id === value?._id}
                  loading={isSearching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Type here to search Buyer'
                      className='c-text-field'
                      variant="standard"
                      size='small'
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        endAdornment: (
                          <>
                            {isSearching || savingCashSale ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option: any) => (
                    <li {...props} key={option._id}>
                      {option.name || option.legalName || option._id}
                    </li>
                  )}
                />
              </div>
            </div>
      </Grid>}

      {/* Notifications tab, right column: Email Notifications */}
      {showNotifications && <Grid item xs={12} md={6}>
            <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10 }}>
              <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Email Notifications</h5>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
                Choose which emails this account sends. Each type can be turned on or off independently.
              </h5>
              <Grid container spacing={1}>
                {EMAIL_NOTIFICATION_OPTIONS.map((option) => (
                  <Grid item xs={12} key={option.key}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 480 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!emailNotifications?.[option.key]}
                            onChange={(e) => handleEmailToggle(option.key, e.target.checked)}
                            disabled={emailNotifications === null || savingEmailKey === option.key}
                            size="small"
                          />
                        }
                        label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                      />
                      {savingEmailKey === option.key ? <CircularProgress size={16} /> : null}
                    </div>
                  </Grid>
                ))}
              </Grid>
            </div>
      </Grid>}

      {/* Notifications tab, full-width: a single "Add Recipients" card that groups the
          Business Summary and Report-sharing recipient sections. Each section appears only when
          its corresponding toggle is ON; the whole card is hidden when none of those toggles are on. */}
      {showNotifications && (!!emailNotifications?.businessSummary || !!waNotifications?.businessSummary || !!emailNotifications?.shareReports) && <Grid item xs={12}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 15 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Add Recipients</h5>
            {(savingRecipients || savingShare) ? <CircularProgress size={16} style={{ marginBottom: 10 }} /> : null}
          </div>
          <Divider sx={{ mt: 0.5, mb: 1.5 }} />

          {/* Section: Business Summary Recipients — shown when either Daily business summary toggle is ON */}
          {(!!emailNotifications?.businessSummary || !!waNotifications?.businessSummary) && <>
            <h5 style={{ fontWeight: 700, color: '#000', fontSize: 14, marginBottom: 6 }}>Business Summary Recipients</h5>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 }}>
              Add extra people to receive the Daily Business Summary, in addition to this account's own email and WhatsApp number.
              Emails are sent only when the Email "Daily business summary" toggle is ON; phone numbers only when the WhatsApp "Daily business summary" toggle is ON.
            </h5>

            <Grid container spacing={3}>
              {/* Additional emails — only when the email Daily business summary toggle is ON */}
              {!!emailNotifications?.businessSummary && <Grid item xs={12} md={6}>
                <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Additional Email Addresses</h5>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 480 }}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Enter email address"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); if (emailError) setEmailError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); } }}
                    error={!!emailError}
                    helperText={emailError || ' '}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddOutlinedIcon />}
                    onClick={handleAddEmail}
                    disabled={savingRecipients || !emailInput.trim()}
                    sx={{ mt: 0.25, textTransform: 'none', color: '#059669', borderColor: '#059669' }}
                  >
                    Add
                  </Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {summaryEmails.length === 0
                    ? <span style={{ fontSize: 13, color: '#9da7b7' }}>No additional emails added.</span>
                    : summaryEmails.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveEmail(email)}
                        disabled={savingRecipients}
                        size="small"
                      />
                    ))}
                </div>
              </Grid>}

              {/* Additional phone numbers — only when the WhatsApp Daily business summary toggle is ON */}
              {!!waNotifications?.businessSummary && <Grid item xs={12} md={6}>
                <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Additional WhatsApp Numbers</h5>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 480 }}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="e.g. +919876543210"
                    value={phoneInput}
                    onChange={(e) => { setPhoneInput(e.target.value); if (phoneError) setPhoneError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPhone(); } }}
                    error={!!phoneError}
                    helperText={phoneError || ' '}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddOutlinedIcon />}
                    onClick={handleAddPhone}
                    disabled={savingRecipients || !phoneInput.trim()}
                    sx={{ mt: 0.25, textTransform: 'none', color: '#059669', borderColor: '#059669' }}
                  >
                    Add
                  </Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {summaryPhones.length === 0
                    ? <span style={{ fontSize: 13, color: '#9da7b7' }}>No additional numbers added.</span>
                    : summaryPhones.map((phone) => (
                      <Chip
                        key={phone}
                        label={phone}
                        onDelete={() => handleRemovePhone(phone)}
                        disabled={savingRecipients}
                        size="small"
                      />
                    ))}
                </div>
              </Grid>}
            </Grid>
          </>}

          {/* Divider between sections, only when both sections are visible */}
          {(!!emailNotifications?.businessSummary || !!waNotifications?.businessSummary) && !!emailNotifications?.shareReports &&
            <Divider sx={{ mt: 2.5, mb: 2 }} />}

          {/* Section: Report Recipients — shown when the "Share reports" email toggle is ON.
              A master list applies to every shareable report; a per-report list adds extras for one report. */}
          {!!emailNotifications?.shareReports && <>
            <h5 style={{ fontWeight: 700, color: '#000', fontSize: 14, marginBottom: 6 }}>Report Recipients</h5>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 }}>
              When you click Share on a report (GSTR-1, TDS, Day Book, Profit &amp; Loss, Balance Sheet), the report
              (PDF and Excel attached) is always emailed to this account's own email. The recipients below are added on top.
            </h5>

            {/* Mode selector — the user configures ONE of these at a time. */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={shareMode}
              onChange={(_e, val) => { if (val) setShareMode(val); }}
              sx={{
                mb: 2,
                '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 13, px: 2 },
                '& .Mui-selected': { backgroundColor: '#059669 !important', color: '#fff !important' },
              }}
            >
              <ToggleButton value="master">All reports</ToggleButton>
              <ToggleButton value="specific">Specific report</ToggleButton>
            </ToggleButtonGroup>

            {/* Master list: applies to all reports */}
            {shareMode === 'master' && (
              <Grid container>
                <Grid item xs={12} md={6}>
                  <EmailListEditor
                    label="Recipients for all reports"
                    emails={masterEmails}
                    onChange={handleMasterEmailsChange}
                    saving={savingShare}
                    emptyText="No shared recipients added."
                  />
                </Grid>
              </Grid>
            )}

            {/* Per-report list: pick a report, edit its extra recipients */}
            {shareMode === 'specific' && (
              <Grid container>
                <Grid item xs={12} md={6}>
                  <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Choose a report</h5>
                  <TextField
                    select
                    size="small"
                    variant="standard"
                    value={selectedReportKey}
                    onChange={(e) => setSelectedReportKey(e.target.value)}
                    sx={{ minWidth: 220, mb: 1.5 }}
                  >
                    {SHARE_REPORT_OPTIONS.map((r) => (
                      <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
                    ))}
                  </TextField>
                  <EmailListEditor
                    label={`Recipients for ${SHARE_REPORT_OPTIONS.find((r) => r.key === selectedReportKey)?.label || ''}`}
                    emails={perReportEmails[selectedReportKey] || []}
                    onChange={(next) => handlePerReportEmailsChange(selectedReportKey, next)}
                    saving={savingShare}
                    emptyText="No report-specific recipients added."
                  />
                </Grid>
              </Grid>
            )}
          </>}
        </div>
      </Grid>}
    </Grid>
  );
};

export default Settings;
